/**
 * Date utilities for task management
 */

/**
 * Get relative due date label for tasks
 * @param {string|Date} dueDate - The due date of the task
 * @param {boolean} isDone - Whether the task is completed
 * @returns {object|null} - { text, color, isOverdue } or null if no due date
 */
export function getRelativeDueLabel(dueDate, isDone = false) {
  if (!dueDate) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const due = new Date(dueDate);
  const dueForComparison = new Date(dueDate);
  dueForComparison.setHours(0, 0, 0, 0);
  
  // Don't show relative date for completed tasks
  if (isDone) return null;
  
  const diffTime = dueForComparison.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Helper to format time (e.g., "11:30 PM")
  const formatTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    // Only show time if it's not midnight (00:00)
    if (hours === 0 && minutes === 0) return null;
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  if (diffDays < 0) {
    // Overdue
    const daysOverdue = Math.abs(diffDays);
    return {
      text: `Overdue by ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'}`,
      color: 'text-red-600',
      isOverdue: true
    };
  } else if (diffDays === 0) {
    // Due today - include time if available
    const time = formatTime(due);
    return {
      text: time ? `Due today @ ${time}` : 'Due today',
      color: 'text-orange-600',
      isOverdue: false
    };
  } else if (diffDays === 1) {
    // Due tomorrow - include time if available
    const time = formatTime(due);
    return {
      text: time ? `Due tomorrow @ ${time}` : 'Due tomorrow',
      color: 'text-blue-600',
      isOverdue: false
    };
  } else if (diffDays <= 7) {
    // Due within a week
    return {
      text: `Due in ${diffDays} days`,
      color: 'text-gray-600',
      isOverdue: false
    };
  } else {
    // Due later
    return {
      text: `Due in ${diffDays} days`,
      color: 'text-gray-500',
      isOverdue: false
    };
  }
}

/**
 * Format a date for display
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted date string
 */
export function formatDate(dateString) {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  
  return date.toLocaleDateString('en-GB', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  });
}
